/**
 * FAQ Panel (Phase 2 — Step 2.16).
 *
 * Searchable FAQ panel from PSD Section 15.
 */

import React, { useState, useMemo } from 'react';
import { FAQ_DATA } from '@/data/faq';
import { CollapsiblePanel } from '../shared';

export function FAQPanel(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = useMemo(() => {
    const cats = new Set(FAQ_DATA.map((f) => f.category));
    return ['all', ...Array.from(cats)];
  }, []);

  const filtered = useMemo(() => {
    let items = FAQ_DATA;

    if (selectedCategory !== 'all') {
      items = items.filter((f) => f.category === selectedCategory);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (f) =>
          f.question.toLowerCase().includes(q) ||
          f.answer.toLowerCase().includes(q),
      );
    }

    return items;
  }, [search, selectedCategory]);

  return (
    <div className="faq-panel" role="region" aria-label="Frequently Asked Questions">
      <h3>Frequently Asked Questions</h3>

      <div className="faq-panel__controls">
        <input
          type="search"
          className="faq-panel__search"
          placeholder="Search FAQ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search FAQ"
        />
        <select
          className="faq-panel__category"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          aria-label="Filter by category"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>
      </div>

      <div className="faq-panel__list">
        {filtered.map((faq) => (
          <CollapsiblePanel key={faq.id} title={faq.question}>
            <p>{faq.answer}</p>
          </CollapsiblePanel>
        ))}
        {filtered.length === 0 && (
          <p className="faq-panel__empty">No matching questions found.</p>
        )}
      </div>
    </div>
  );
}
